import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../supabase';
import { useLanguage } from '../i18n/LanguageContext';
import { useUserLanguage } from '../i18n/UserLanguageContext';
import { TOPICS, LEVELS, getTopicName, getTopicDesc } from '../data/topicCatalog';
import { lessonTotal, lessonsDone, isLevelUnlocked } from '../data/topicLessons';
import ProfileMenu from '../ProfileMenu';
import { languageLabel, languageFlag } from '../data/languageNames';
import './Topics.css';

const levelColors = {
    A1: { bg: '#E1F5EE', border: '#9FE1CB', text: '#085041', badge: '#9FE1CB' },
    A2: { bg: '#E6F1FB', border: '#B5D4F4', text: '#0C447C', badge: '#B5D4F4' },
    B1: { bg: '#EEEDFE', border: '#CECBF6', text: '#3C3489', badge: '#CECBF6' },
    B2: { bg: '#FAEEDA', border: '#FAC775', text: '#633806', badge: '#FAC775' },
    C1: { bg: '#FAECE7', border: '#F5C4B3', text: '#993C1D', badge: '#F5C4B3' },
    C2: { bg: '#FAEEDA', border: '#FAC775', text: '#633806', badge: '#FAC775' },
};

function Topics() {
    const navigate = useNavigate();
    const { t, interfaceLanguage } = useLanguage();
    const [activeLevel, setActiveLevel] = useState('all');
    const [search, setSearch] = useState('');

    const { activeLanguage, userLanguages } = useUserLanguage();
    const targetLang = activeLanguage || localStorage.getItem('vozoly_target_language') || 'italian';
    const userLevel = userLanguages.find(l => l.target_language === activeLanguage)?.level || localStorage.getItem('vozoly_level') || 'B1';

    const [completedByTopic, setCompletedByTopic] = useState({});
    const [progressLoaded, setProgressLoaded] = useState(false);

    // Finished lessons for the language being learned, used for the progress bars and for opening the next level
    useEffect(() => {
        const loadProgress = async () => {
            const { data: { user } } = await supabase.auth.getUser();
            if (!user) return;
            const lang = (activeLanguage || localStorage.getItem('vozoly_target_language') || 'italian').toLowerCase();
            const { data } = await supabase
                .from('user_progress')
                .select('topic_slug, lesson_slug')
                .eq('user_id', user.id)
                .eq('target_language', lang);
            const byTopic = {};
            (data || []).forEach(row => {
                if (!byTopic[row.topic_slug]) byTopic[row.topic_slug] = [];
                byTopic[row.topic_slug].push(row.lesson_slug);
            });
            setCompletedByTopic(byTopic);
            setProgressLoaded(true);
        };
        loadProgress();
    }, [activeLanguage]);

    const displayName = localStorage.getItem('vozoly_display_name') || 'U';
    const userInitial = displayName[0].toUpperCase();

    const userLevelIndex = LEVELS.indexOf(userLevel);

    // A level opens at the learner's own level, or once half of the level before it is finished
    const isLevelLocked = (level) => {
        if (!progressLoaded) return LEVELS.indexOf(level) > userLevelIndex;
        return !isLevelUnlocked(level, userLevel, completedByTopic);
    };

    // Every language shares the same topic list
    const getFilteredTopics = () => {
        let result = [];
        LEVELS.forEach(level => {
            const filtered = TOPICS.filter(topic => {
                if (topic.level !== level) return false;
                const name = getTopicName(topic, interfaceLanguage);
                const desc = getTopicDesc(topic, interfaceLanguage);
                return name.toLowerCase().includes(search.toLowerCase()) ||
                    desc.toLowerCase().includes(search.toLowerCase());
            });
            if (filtered.length > 0) {
                result.push({ level, topics: filtered });
            }
        });
        if (activeLevel !== 'all') {
            result = result.filter(r => r.level === activeLevel);
        }
        return result;
    };

    const filteredGroups = getFilteredTopics();

    return (
        <div className="topics-screen">

            <div className="topics-top-bar">
                <div className="topics-top-row">
                    <div className="topics-title">{t('topics.title')}</div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <div className="topics-lang-pill">
                            {languageFlag(targetLang)} {languageLabel(targetLang, interfaceLanguage)}
                        </div>
                        <ProfileMenu initial={userInitial} />
                    </div>
                </div>
                <div className="topics-search">
                    <span className="topics-search-icon">🔍</span>
                    <input
                        className="topics-search-input"
                        type="text"
                        placeholder={t('topics.searchPlaceholder')}
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                    />
                    {search && (
                        <button className="topics-search-clear" onClick={() => setSearch('')}>✕</button>
                    )}
                </div>
            </div>

            <div className="topics-level-strip">
                <button
                    className={`topics-level-btn ${activeLevel === 'all' ? 'active' : ''}`}
                    onClick={() => setActiveLevel('all')}
                >
                    {t('topics.allLevels')}
                </button>
                {LEVELS.map(level => {
                    const locked = isLevelLocked(level);
                    return (
                        <button
                            key={level}
                            className={`topics-level-btn ${activeLevel === level ? 'active' : ''} ${locked ? 'locked' : ''}`}
                            onClick={() => setActiveLevel(level)}
                        >
                            {level} {locked ? '🔒' : ''}
                        </button>
                    );
                })}
            </div>

            <div className="topics-content">
                {filteredGroups.length === 0 ? (
                    <div className="topics-empty">
                        <div className="topics-empty-icon">🔍</div>
                        <div className="topics-empty-title">{t('topics.noTopicsFound')}</div>
                        <div className="topics-empty-desc">{t('topics.tryDifferentSearch')}</div>
                    </div>
                ) : (
                    filteredGroups.map(({ level, topics }) => {
                        const locked = isLevelLocked(level);
                        const lc = levelColors[level] || levelColors.B1;
                        return (
                            <div key={level} className="topics-group">
                                <div className="topics-group-header">
                                    <div
                                        className="topics-group-badge"
                                        style={{ background: lc.badge, color: lc.text }}
                                    >
                                        {level}
                                    </div>
                                    <div className="topics-group-label" style={{ color: lc.text }}>
                                        {t(`topics.levelNames.${level}`)}
                                    </div>
                                    {locked && <div className="topics-group-lock">🔒 {t('topics.locked')}</div>}
                                </div>

                                <div className="topics-grid">
                                    {topics.map(topic => (
                                        <div
                                            key={topic.slug}
                                            className={`topics-card ${locked ? 'locked' : ''}`}
                                            onClick={() => {
                                                if (locked) return;
                                                navigate(`/topic/${topic.slug}`);
                                            }}
                                        >
                                            <div className="topics-card-top">
                                                <div className="topics-card-emoji">{topic.emoji}</div>
                                                {locked && <div className="topics-card-lock">🔒</div>}
                                            </div>
                                            <div className="topics-card-name">
                                                {getTopicName(topic, interfaceLanguage)}
                                            </div>
                                            <div className="topics-card-desc">{getTopicDesc(topic, interfaceLanguage)}</div>
                                            <div className="topics-card-footer">
                                                <div
                                                    className="topics-card-level"
                                                    style={{ background: lc.badge, color: lc.text }}
                                                >
                                                    {level}
                                                </div>
                                                <div className="topics-card-progress">
                                                    {locked ? t('topics.locked') : `${lessonsDone(topic.slug, completedByTopic)} / ${lessonTotal(topic.slug)}`}
                                                </div>
                                            </div>
                                            {!locked && (
                                                <div className="topics-card-bar">
                                                    <div
                                                        className="topics-card-bar-fill"
                                                        style={{
                                                            width: `${Math.round((lessonsDone(topic.slug, completedByTopic) / lessonTotal(topic.slug)) * 100)}%`,
                                                            background: lc.border,
                                                        }}
                                                    ></div>
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        );
                    })
                )}
            </div>

            <div style={{ height: '80px' }}></div>

            <div className="topics-bottom-nav">
                <button className="topics-nav-item" onClick={() => navigate('/home')}>
                    <div className="topics-nav-icon">🏠</div>
                    <div className="topics-nav-label">{t('nav.home')}</div>
                </button>
                <button className="topics-nav-item active">
                    <div className="topics-nav-icon">📚</div>
                    <div className="topics-nav-label">{t('nav.topics')}</div>
                    <div className="topics-nav-dot"></div>
                </button>
                <button className="topics-nav-item" onClick={() => navigate('/grammar')}>
                    <div className="topics-nav-icon nav-icon-grammar">📐</div>
                    <div className="topics-nav-label">{t('nav.grammar')}</div>
                </button>
                <button className="topics-nav-item" onClick={() => navigate('/wordbank')}>
                    <div className="topics-nav-icon">📒</div>
                    <div className="topics-nav-label">{t('nav.wordbank')}</div>
                </button>
                <button className="topics-nav-item" onClick={() => navigate('/games')}>
                    <div className="topics-nav-icon">🎮</div>
                    <div className="topics-nav-label">{t('nav.games')}</div>
                </button>
            </div>

        </div>
    );
}

export default Topics;